import chroma from 'chroma-js';
import { PaletteName } from 'styles/theme';
import { getTimeInterval } from 'helpers/chartHelper';
import { isDate } from 'helpers/helpers';
import {
    ColorMapping,
    TimeScales as TS,
    SlidingTimeScales as STS,
    ChartSettingsT,
    OrderedColumnsT,
} from 'business/definitions';
import {
    SerieT,
    Dataset,
} from 'components/ChartContainer/components/Chart/types';
import { getYearlyComparisonSettingsForScale } from './yearlyComparison/settings';
import { isYearlyComparisonScale } from './yearlyComparison/scale';

import './eChartHelper.scss';

export function getInterval(
    type: DataTypesT,
    scale: TimeScalesT | null | undefined,
    values: number[],
): number {
    if (type === 'time') {
        const HALF_HOUR_IN_MICROSECONDS = 1000 * 60 * 30;
        return HALF_HOUR_IN_MICROSECONDS;
    }
    if (isDate(type) && scale) {
        console.warn(
            `getTimeInterval called with values: ${type} ${scale} - should not happen`,
        );
        return getTimeInterval(scale);
    }
    values.sort((a, b) => a - b);
    const differences = values.slice(1).map((x, i) => x - values[i]);
    return Math.min(...differences);
}

/**
 * Calculate bins for charts with a temporal axis.
 * The problem is that the interval between each ticks varies, as months,
 * quarters or even years don't always have the same number of seconds.
 */
export function getBins(
    type: DataTypesT,
    scale: TimeScalesT | undefined,
    xValues: number[],
): Array<number> {
    const bins = [];
    let currentValue = Math.min(...xValues);
    const currentDate = new Date(currentValue);
    const maxValue = Math.max(...xValues);
    if (
        scale === TS.schoolYear ||
        scale === TS.year ||
        scale === STS.slidingYear
    ) {
        while (currentValue <= maxValue) {
            bins.push(currentValue);
            currentDate.setFullYear(currentDate.getFullYear() + 1);
            currentValue = currentDate.getTime();
        }
        return bins;
    }
    if (scale === TS.quarter) {
        while (currentValue <= maxValue) {
            bins.push(currentValue);
            currentDate.setMonth(currentDate.getMonth() + 3);
            currentValue = currentDate.getTime();
        }
        return bins;
    }
    if (scale === TS.month) {
        // TODO do we want to set the day and hours to 1 and 00:00 ?
        while (currentValue <= maxValue) {
            bins.push(currentValue);
            currentDate.setMonth(currentDate.getMonth() + 1);
            // currentDate.setHours(0); // TODO is this useful ?
            currentValue = currentDate.getTime();
        }
        return bins;
    }
    if (scale === TS.week || scale === STS.slidingWeek) {
        while (currentValue <= maxValue) {
            bins.push(currentValue);
            currentDate.setDate(currentDate.getDate() + 7);
            currentValue = currentDate.getTime();
        }
        return bins;
    }
    if (scale === TS.day) {
        while (currentValue <= maxValue) {
            bins.push(currentValue);
            currentDate.setDate(currentDate.getDate() + 1);
            currentValue = currentDate.getTime();
        }
        return bins;
    }

    const interval = getInterval(type, scale, xValues);
    while (currentValue <= maxValue) {
        bins.push(currentValue);
        currentValue += interval;
    }
    return bins;
}

export type ChartOptionsArgsT = {
    series: SerieT[];
    datasets?: Dataset[];
    ticks: number[] | null | undefined;
    color: string[];
    xKey: string;
    xType: DataTypesT;
    xTitle: string;
    xScale: TimeScalesT | null | undefined;
    xInterval: number | null | undefined;
    yKey: string;
    yType: DataTypesT;
    yTitle: string;
    secondaryYAxis?: string;
    isYearlyComparison?: boolean;
    xAxisData?: number[] | string[];
    isStack?: boolean;
    showLegend?: boolean;
    showLegendOnRightSide?: boolean;
    areLabelsShown?: boolean;
    areLabelsShownInside?: boolean;
    areLabelsVertical?: boolean;
    isEvolutionDisplayed?: boolean;
    areTicksRotated?: boolean;
    goalValue?: number;
    customColorMappings?: ColorMapping[];
    isPdfModeActivated: boolean;
    isPngModeActivated: boolean;
    decimalPrecision: 0 | 1 | 2;
    displayBy: ChartSettingsT['displayBy'];
    treeValueTranslations: Record<string, string>;
    wrapVerticalAxisLabels?: boolean;
    verticalAxisWidth?: 'sm' | 'md' | 'lg';
    axisLabelFontSize?: 'sm' | 'md' | 'lg' | 'default';
    useGradientColor?: boolean;
    reverseGradientColor?: boolean;
    maxValue?: number;
    palette?: PaletteName;
    isDataZoomEnabled?: boolean;
    dataZoomInterval?: [number, number];
    isChartViewedOnDashboard?: boolean;
};

export function getTooltipPosition(
    pos: number[],
    params: FlowAnyObject[],
    el: Element,
    elRect: FlowAnyObject,
    size: FlowAnyObject,
): string | FlowAnyObject {
    // TODO: improve typing
    // when the tooltip is very wide or tall, we position it in a corner
    // so that it stays visible
    const isTooltipTooBig =
        el.scrollHeight > (size.viewSize[1] * 3) / 4 ||
        el.scrollWidth > (size.viewSize[0] * 3) / 4;
    if (isTooltipTooBig) {
        const obj = { top: 10 };
        // code taken from the echartjs doc https://echarts.apache.org/en/option.html#tooltip
        // https://echarts.apache.org/examples/en/editor.html?c=doc-example/candlestick-axisPointer
        // @ts-expect-error [TS migration] (previously $FlowFixMe)
        obj[['left', 'right'][+(pos[0] < size.viewSize[0] / 2)]] = 30;
        return obj;
    }
    return 'inside';
}

export function getContrastedColors(colors: string[]): Array<string | void> {
    let maxContrast = 0;
    let lightColor;
    let darkColor;

    colors.forEach((colorOne) => {
        colors.forEach((colorTwo) => {
            const contrast = chroma.distance(colorOne, colorTwo);
            if (contrast > maxContrast) {
                maxContrast = contrast;
                darkColor = colorOne;
                lightColor = colorTwo;
            }
        });
    });
    return [lightColor, darkColor];
}

/**
 * Sort the series of a chart so that it is correctly sorted by month, quarter, year,
 * depending on what was choosen has the Z axis (Series in the UI).
 * The sort is in-place so this function doesn't return anything and will not touch the array
 * if the Z column is not defined and valid for a yearly comparision sort.
 * @param series - the series to sort.
 * @param orderedColumns - the column of the chart.
 */
export const sortSeriesForYearlyComparision = (
    series: SerieT[],
    orderedColumns: OrderedColumnsT,
) => {
    if (orderedColumns.z && isYearlyComparisonScale(orderedColumns.z.key)) {
        const sortSetting = getYearlyComparisonSettingsForScale(
            orderedColumns.z.key,
        );
        if (sortSetting) {
            series.sort((a, b) => sortSetting.sort(a.name, b.name));
        }
    }
};

export const isDictAllDataValueT = (
    value: NotNullDataValueT | null,
): value is DictAllDataValueT =>
    typeof value === 'object' &&
    value !== null &&
    'value' in value &&
    'label' in value;

/***
 * UnNest data if nested; also fills treeValueTranslations {[key:string] : {value : label}}
 *
 */
export const getUnNestedDataFillingTranslationTree = (
    data: ResponseDataT[],
    treeValueTranslationsPerField: Record<string, Record<string, string>>,
) => {
    const unestDatum = (value: ResponseDataT): ResponseDataT => {
        const unNested: ResponseDataT = {};
        Object.keys(value).map((e) => {
            if (isDictAllDataValueT(value[e])) {
                if (!(e in treeValueTranslationsPerField))
                    treeValueTranslationsPerField[e] = {};
                treeValueTranslationsPerField[e][value[e].value as string] =
                    value[e].label;
                unNested[e] = value[e].value;
            } else unNested[e] = value[e];
        });
        return unNested;
    };
    const unestData = (values: ResponseDataT[]) =>
        values.map((val) => unestDatum(val));
    return unestData(data);
};

// https://github.com/apache/echarts/issues/14723
// We build the type because the type is not exported as an Echart type
type EchartTooltipFormatterParams<DataType> = {
    componentType: string;
    data: DataType;
};
export function isEChartTooltipFormatterParams<T>(
    a: unknown,
): a is EchartTooltipFormatterParams<T> {
    if (a !== null && typeof a === 'object') {
        return 'componentType' in a && 'data' in a;
    }
    return false;
}
